import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Wrench, ExternalLink, Download, Loader2, CheckCircle, AlertCircle, CheckSquare, Clock, Zap, Check, Edit2, FileText } from 'lucide-react';
import { useProcessorPrompts } from '../../hooks/useProcessorPrompts';
import { usePipelineBuilderStore } from '../../store/pipelineBuilderStore';
import { PromptItem } from './PromptItem';
import { PromptEditorModal } from './PromptEditorModal';
import { PromptData } from '../../types/pipeline';
import api from '../../services/api';

// Get API base URL (matches ApiService configuration)
const getApiBaseUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  if (apiUrl) {
    return `${apiUrl}/api`;
  } else if (process.env.NODE_ENV === "production") {
    return "https://api.roboworks.com/api";
  } else {
    return "http://localhost:8000/api";
  }
};

interface QuickEditDialogProps {
  nodeId: string;
  onClose: () => void;
}

export const QuickEditDialog: React.FC<QuickEditDialogProps> = ({ nodeId, onClose }) => {
  const { nodes, updateNode } = usePipelineBuilderStore();
  const node = nodes.find(n => n.id === nodeId);
  const processorPrompts = useProcessorPrompts(node?.data.processor_name);
  
  const [editingPrompt, setEditingPrompt] = useState<{
    name: string;
    data: PromptData;
    currentContent: string;
  } | null>(null);
  const [nodeName, setNodeName] = useState(node?.data.name || '');
  
  // A2A helper state (SWRM-133)
  const [a2aEndpointUrl, setA2aEndpointUrl] = useState('');
  const [isFetchingAgentCard, setIsFetchingAgentCard] = useState(false);
  const [fetchAgentCardStatus, setFetchAgentCardStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [fetchAgentCardError, setFetchAgentCardError] = useState('');

  // LLM configuration state
  const [llmProviders, setLlmProviders] = useState<Array<{ id: string; name: string; type: string; model: string; enabled: boolean; default: boolean }>>([]);
  const [llmProvidersLoading, setLlmProvidersLoading] = useState(false);
  const [llmTimeoutInput, setLlmTimeoutInput] = useState<string>('');

  // Initialize LLM timeout input from node config
  useEffect(() => {
    if (node) {
      const timeout = node.data.config?.llm_timeout || 300;
      setLlmTimeoutInput(String(timeout));
    }
  }, [node]);

  // Fetch LLM providers on mount
  useEffect(() => {
    const fetchProviders = async () => {
      setLlmProvidersLoading(true);
      try {
        const data = await api.getLLMProviders();
        setLlmProviders(data);
      } catch (error) {
        console.error('Failed to fetch LLM providers:', error);
        setLlmProviders([]);
      } finally {
        setLlmProvidersLoading(false);
      }
    };

    fetchProviders();
  }, []);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleEditPrompt = useCallback((promptName: string) => {
    if (!processorPrompts || !processorPrompts.prompts?.prompts || !node) return;

    const promptData = (processorPrompts.prompts.prompts as any)[promptName];
    if (!promptData) return;

    // Get current content from config.prompts or use default
    const currentConfig = node.data.config || {};
    const currentPrompts = currentConfig.prompts || {};
    // Use !== undefined to preserve empty strings
    const currentContent = currentPrompts[promptName] !== undefined ? currentPrompts[promptName] : promptData.content;

    // Include custom inputs as available variables (for dynamic input processors)
    const customInputNames = node.data.custom_inputs?.map((ci: { name: string; description?: string }) => ci.name) || [];
    
    // Check if processor supports dynamic inputs
    const supportsDynamicInputs = node.data.config?.supports_dynamic_inputs === true ||
                                   ['oneshot_document', 'agentic_task', 'orchestrator', 'vector', 'shell'].includes(node.data.processor_name);
    
    // For dynamic input processors: ONLY custom_inputs are valid (input_artifact_keys is just wiring)
    // For regular processors: input_artifact_keys proves the input is connected
    const inputArtifactNames = supportsDynamicInputs ? [] : Object.keys(node.data.input_artifact_keys || {});
    
    const allVariables = [...promptData.variables, ...customInputNames, ...inputArtifactNames];

    // Mark optional inputs (from processor metadata) as optional
    const optionalInputs = node.data.optional_input_keys || [];
    // For dynamic input processors, custom inputs are required (user explicitly added them to use in prompt)
    // For traditional processors, custom inputs may be optional
    const optionalVariables = supportsDynamicInputs 
      ? [...optionalInputs] // Dynamic input processors: custom_inputs are required
      : [...optionalInputs, ...customInputNames]; // Traditional: custom inputs are optional

    setEditingPrompt({
      name: promptName,
      data: {
        ...promptData,
        variables: allVariables,
        optional_variables: optionalVariables, // Mark which ones are optional
      },
      currentContent,
    });
  }, [processorPrompts, node]);

  const handleSavePrompt = useCallback((content: string) => {
    if (!editingPrompt || !node) return;

    const currentConfig = node.data.config || {};
    const currentPrompts = currentConfig.prompts || {};

    // Update the prompt
    const updatedPrompts = {
      ...currentPrompts,
      [editingPrompt.name]: content,
    };

    // Update node config
    updateNode(node.id, {
      data: {
        ...node.data,
        config: {
          ...currentConfig,
          prompts: updatedPrompts,
        },
      },
    });

    // Close modal
    setEditingPrompt(null);
  }, [editingPrompt, node, updateNode]);
  
  const handleDynamicOutputsChange = useCallback((outputs: Array<{name: string, type: string}>) => {
    if (!node) return;
    
    // Update node data with new dynamic outputs
    // Update BOTH data.dynamic_outputs (for UI) and config.dynamic_outputs (for persistence)
    updateNode(node.id, {
      data: {
        ...node.data,
        dynamic_outputs: outputs,
        config: {
          ...node.data.config,
          dynamic_outputs: outputs,
        },
      },
    });
  }, [node, updateNode]);

  const handleClosePromptEditor = useCallback(() => {
    setEditingPrompt(null);
  }, []);

  const handleSaveNodeName = useCallback(() => {
    if (!node) return;
    updateNode(node.id, {
      data: {
        ...node.data,
        name: nodeName,
      },
    });
  }, [node, nodeName, updateNode]);

  const handleLlmProviderChange = useCallback((providerId: string) => {
    if (!node) return;
    const newConfig = {
      ...(node.data.config || {}),
      llm_provider_id: providerId,
    };
    updateNode(node.id, {
      data: {
        ...node.data,
        config: newConfig,
      },
    });
  }, [node, updateNode]);

  const handleLlmTimeoutInputChange = useCallback((value: string) => {
    // Allow typing any value (including empty string for clearing)
    setLlmTimeoutInput(value);
  }, []);

  const handleLlmTimeoutBlur = useCallback(() => {
    if (!node) return;
    
    // Parse and validate the timeout value
    const parsed = parseInt(llmTimeoutInput, 10);
    let finalValue: number;
    
    if (isNaN(parsed) || parsed < 10) {
      // If invalid or too small, use minimum (10)
      finalValue = 10;
    } else if (parsed > 3600) {
      // If too large, use maximum (3600)
      finalValue = 3600;
    } else {
      finalValue = parsed;
    }
    
    // Update local state to reflect the validated value
    setLlmTimeoutInput(String(finalValue));
    
    // Update node config
    const newConfig = {
      ...(node.data.config || {}),
      llm_timeout: finalValue,
    };
    updateNode(node.id, {
      data: {
        ...node.data,
        config: newConfig,
      },
    });
  }, [node, updateNode, llmTimeoutInput]);

  const handleOpenToolsTab = useCallback(() => {
    if (!node) return;
    
    // Dispatch custom event to open properties panel on Tools tab
    const event = new CustomEvent('openPropertiesPanelTab', {
      detail: { nodeId: node.id, tab: 'tools' }
    });
    window.dispatchEvent(event);
    
    // Close quick edit dialog
    onClose();
  }, [node, onClose]);

  const handleOpenTasksTab = useCallback(() => {
    if (!node) return;
    
    // Dispatch custom event to open properties panel on Tasks tab
    const event = new CustomEvent('openPropertiesPanelTab', {
      detail: { nodeId: node.id, tab: 'tasks' }
    });
    window.dispatchEvent(event);
    
    // Close quick edit dialog
    onClose();
  }, [node, onClose]);

  const handleOpenCommandsTab = useCallback(() => {
    if (!node) return;
    
    // Dispatch custom event to open properties panel on Commands tab
    const event = new CustomEvent('openPropertiesPanelTab', {
      detail: { nodeId: node.id, tab: 'commands' }
    });
    window.dispatchEvent(event);
    
    // Close quick edit dialog
    onClose();
  }, [node, onClose]);

  // A2A agent card fetcher (SWRM-133)
  const handleFetchAgentCard = useCallback(async () => {
    if (!a2aEndpointUrl.trim()) {
      setFetchAgentCardStatus('error');
      setFetchAgentCardError('Please enter an endpoint URL');
      return;
    }

    setIsFetchingAgentCard(true);
    setFetchAgentCardStatus('idle');
    setFetchAgentCardError('');

    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(
        `${baseUrl}/a2a/fetch-agent-card?endpoint_url=${encodeURIComponent(a2aEndpointUrl.trim())}&timeout=10`
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch agent card' }));
        throw new Error(errorData.detail || 'Failed to fetch agent card');
      }

      const agentCard = await response.json();

      // Parse input schema to determine required and optional inputs
      const inputSchema = agentCard.input_schema;
      const requiredInputs: string[] = [];
      const optionalInputs: string[] = [];

      if (inputSchema && inputSchema.properties) {
        // Get schema-level required array (correct JSON Schema format)
        const requiredFields = inputSchema.required || [];
        Object.entries(inputSchema.properties).forEach(([key, prop]: [string, any]) => {
          if (requiredFields.includes(key)) {
            requiredInputs.push(key);
          } else {
            optionalInputs.push(key);
          }
        });
      }

      // Generate config from agent card
      const config = {
        endpoint_url: agentCard.endpoint || a2aEndpointUrl.trim(),
        agent_name: agentCard.name || '',
        auth_type: agentCard.authentication ? (agentCard.authentication.type === 'bearer' ? 'bearer' : 'api_key') : 'none',
        auth_token: '',
        timeout_seconds: 30,
        max_retries: 3,
        input_schema: inputSchema, // Store schema for reference
      };

      // Update the node's config AND input keys to show handles
      if (node) {
        updateNode(node.id, {
          data: {
            ...node.data,
            config,
            required_input_keys: requiredInputs,
            optional_input_keys: optionalInputs,
          },
        });
      }

      setFetchAgentCardStatus('success');
      setTimeout(() => setFetchAgentCardStatus('idle'), 3000);
    } catch (error: any) {
      console.error('Failed to fetch agent card:', error);
      setFetchAgentCardStatus('error');
      setFetchAgentCardError(error.message || 'Failed to fetch agent card');
    } finally {
      setIsFetchingAgentCard(false);
    }
  }, [a2aEndpointUrl, node, updateNode]);

  if (!node) return null;

  // Find user prompt - need to go deeper: processorPrompts.prompts.prompts
  const promptsDict = processorPrompts?.prompts?.prompts;
  const userPromptName = promptsDict
    ? Object.keys(promptsDict).find(name => 
        (promptsDict as any)[name].type === 'user'
      )
    : null;

  // Check if this is an agentic processor (has tools tab)
  const isAgenticProcessor = node.data.processor_name === 'agentic_task' || 
                             node.data.processor_name === 'orchestrator' ||
                             node.data.processor_name === 'vector';
  
  // Check if this processor uses LLM (has llm_provider_id and llm_timeout config)
  const isLlmProcessor = node.data.processor_name === 'agentic_task' || 
                         node.data.processor_name === 'orchestrator' ||
                         node.data.processor_name === 'oneshot_document';
  
  // Get tool configuration
  const allowedTools = node.data.config?.allowed_tools;
  const toolsStatus = allowedTools === null || allowedTools === undefined
    ? 'All tools available'
    : `${allowedTools.length} tool${allowedTools.length === 1 ? '' : 's'} selected`;

  // Get preset tasks configuration
  const presetTasks = node.data.config?.preset_tasks || [];
  const tasksStatus = presetTasks.length === 0
    ? 'No preset tasks'
    : `${presetTasks.length} task${presetTasks.length === 1 ? '' : 's'} configured`;

  return createPortal(
    <>
      <div 
        className="quick-edit-dialog-wrapper fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div 
          className="quick-edit-dialog-container bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col border border-gray-200 dark:border-gray-700 relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button - top right corner */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors z-10"
            title="Close"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Display Name Section */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Display Name
              </h3>
              <input
                type="text"
                value={nodeName}
                onChange={(e) => setNodeName(e.target.value)}
                onBlur={handleSaveNodeName}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveNodeName();
                    e.currentTarget.blur();
                  }
                }}
                className="w-full px-4 py-2 text-base text-gray-900 dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="Processor name"
              />
            </div>

            {/* Advanced Settings - Shared Conversation History (SWRM-158) */}
            {/* Only show for agentic processors that are inside a sub-agent chain */}
            {isAgenticProcessor && (node.data.orchestratorNodeId || node.data.subAgentName) && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Advanced Settings
                </h3>
                <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={node.data.config?.preserve_conversation_history || false}
                      onChange={(e) => {
                        const newConfig = {
                          ...(node.data.config || {}),
                          preserve_conversation_history: e.target.checked
                        };
                        updateNode(node.id, {
                          data: {
                            ...node.data,
                            config: newConfig
                          }
                        });
                      }}
                      className="mt-0.5 w-4 h-4 text-blue-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Preserve conversation history across invocations
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        When this sub-agent is called multiple times by its orchestrator, maintain a single shared conversation history. 
                        This allows iterative refinement with full context from previous executions.
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* A2A Agent Configuration Helper (SWRM-133) - only for a2a_agent processor */}
            {node.data.processor_name === 'a2a_agent' && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Agent Configuration
                </h3>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Download className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100">
                      Fetch Agent Card
                    </h4>
                  </div>
                  
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    Enter the external agent's endpoint URL to automatically fetch its configuration
                  </p>

                  <div className="space-y-2">
                    {/* URL input and button side-by-side */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={a2aEndpointUrl}
                        onChange={(e) => setA2aEndpointUrl(e.target.value)}
                        placeholder="https://external-agent.example.com"
                        disabled={isFetchingAgentCard}
                        className="flex-1 px-3 py-2 border border-blue-300 dark:border-blue-700 rounded-md
                          bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm
                          disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed
                          focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />

                      <button
                        onClick={handleFetchAgentCard}
                        disabled={isFetchingAgentCard || !a2aEndpointUrl.trim()}
                        className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium
                          bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors
                          disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {isFetchingAgentCard ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Fetching...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4" />
                            Fetch
                          </>
                        )}
                      </button>
                    </div>

                    {fetchAgentCardStatus === 'success' && (
                      <div className="flex items-center gap-2 text-xs text-green-700 dark:text-green-400">
                        <CheckCircle className="w-4 h-4" />
                        Configuration generated! Open Properties Panel to review.
                      </div>
                    )}

                    {fetchAgentCardStatus === 'error' && (
                      <div className="flex items-start gap-2 text-xs text-red-700 dark:text-red-400">
                        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>{fetchAgentCardError}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* User Prompt Section (with LLM Configuration for LLM processors) */}
            {isLlmProcessor ? (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  User Prompt
                </h3>
                <div className="space-y-4">
                  {/* User Prompt Preview/Edit */}
                  {userPromptName && promptsDict ? (
                    <div 
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          {/* Header */}
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {userPromptName}
                            </span>
                            <span className="text-xs px-2 py-0.5 rounded bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                              user
                            </span>
                            {((node.data.config?.prompts?.[userPromptName] || (promptsDict as any)[userPromptName].content) !== (promptsDict as any)[userPromptName].content) && (
                              <span
                                className="text-xs px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 flex items-center gap-1"
                                title="This prompt has been customized"
                              >
                                <Check className="w-3 h-3" />
                                Custom
                              </span>
                            )}
                          </div>

                          {/* Description */}
                          {(promptsDict as any)[userPromptName].description && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                              {(promptsDict as any)[userPromptName].description}
                            </p>
                          )}

                          {/* Preview - expanded to ~3 lines (250 chars) */}
                          <p className="text-xs text-gray-600 dark:text-gray-300 font-mono bg-gray-100 dark:bg-gray-900 p-2 rounded whitespace-pre-wrap line-clamp-3">
                            {(() => {
                              const content = node.data.config?.prompts?.[userPromptName] || (promptsDict as any)[userPromptName].content;
                              return content.length > 250 ? content.substring(0, 250) + '...' : content;
                            })()}
                          </p>

                          {/* Variables */}
                          {(promptsDict as any)[userPromptName].variables.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {(promptsDict as any)[userPromptName].variables.slice(0, 3).map((variable: string) => (
                                <code
                                  key={variable}
                                  className="text-xs bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded text-gray-700 dark:text-gray-300"
                                >
                                  {`{{${variable}}}`}
                                </code>
                              ))}
                              {(promptsDict as any)[userPromptName].variables.length > 3 && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  +{(promptsDict as any)[userPromptName].variables.length - 3} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Edit Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditPrompt(userPromptName);
                          }}
                          className="flex-shrink-0 p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                          title="Edit prompt"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                      This processor has no user prompt.
                    </p>
                  )}

                  {/* LLM Provider Dropdown */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      <Zap className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                      LLM Provider
                    </label>
                    {llmProvidersLoading ? (
                      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading providers...
                      </div>
                    ) : (
                      <select
                        value={node.data.config?.llm_provider_id || ''}
                        onChange={(e) => handleLlmProviderChange(e.target.value)}
                        className="w-full px-3 py-2 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                      >
                        <option value="">Default Provider</option>
                        {llmProviders.map((provider) => (
                          <option key={provider.id} value={provider.id}>
                            {provider.name} ({provider.model})
                          </option>
                        ))}
                      </select>
                    )}
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Select which LLM provider this processor should use
                    </div>
                  </div>

                  {/* LLM Timeout Input */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      LLM Request Timeout
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="10"
                        max="3600"
                        value={llmTimeoutInput}
                        onChange={(e) => handleLlmTimeoutInputChange(e.target.value)}
                        onBlur={handleLlmTimeoutBlur}
                        className="flex-1 px-3 py-2 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                      <span className="text-sm text-gray-600 dark:text-gray-400">seconds</span>
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Maximum time for each LLM API request (10-3600 seconds, default: 300)
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              userPromptName && promptsDict ? (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    User Prompt
                  </h3>
                  <PromptItem
                    promptName={userPromptName}
                    promptData={(promptsDict as any)[userPromptName]}
                    currentContent={
                      node.data.config?.prompts?.[userPromptName] || 
                      (promptsDict as any)[userPromptName].content
                    }
                    onEdit={() => handleEditPrompt(userPromptName)}
                  />
                </div>
              ) : (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    User Prompt
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                    This processor has no user prompt.
                  </p>
                </div>
              )
            )}

            {/* Tools Section - only for agentic processors */}
            {isAgenticProcessor && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Tools
                </h3>
                <button
                  onClick={handleOpenToolsTab}
                  className="w-full px-4 py-3 text-left bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Wrench className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {toolsStatus}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          Click to configure tool access
                        </div>
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
                  </div>
                </button>
              </div>
            )}

            {/* Tasks Section - only for agentic processors */}
            {isAgenticProcessor && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Preset Tasks
                </h3>
                <button
                  onClick={handleOpenTasksTab}
                  className="w-full px-4 py-3 text-left bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckSquare className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {tasksStatus}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          Click to configure preset tasks
                        </div>
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
                  </div>
                </button>
              </div>
            )}

            {/* Commands Section - only for shell processor */}
            {node?.data.processor_name === 'shell' && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Shell Commands
                </h3>
                <button
                  onClick={handleOpenCommandsTab}
                  className="w-full px-4 py-3 text-left bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {(() => {
                            try {
                              const config = node.data.config || {};
                              const commands = config.commands || [];
                              return commands.length === 0 ? 'No commands configured' : `${commands.length} command${commands.length !== 1 ? 's' : ''} configured`;
                            } catch {
                              return 'No commands configured';
                            }
                          })()}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          Click to configure shell commands
                        </div>
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Prompt Editor Modal */}
      {editingPrompt && (
        <PromptEditorModal
          isOpen={true}
          promptName={editingPrompt.name}
          promptData={editingPrompt.data}
          currentContent={editingPrompt.currentContent}
          processorName={node.data.processor_name}
          standardOutputKeys={node.data.output_keys || []}
          onSave={handleSavePrompt}
          onClose={handleClosePromptEditor}
          onOutputsChange={handleDynamicOutputsChange}
        />
      )}
    </>,
    document.body
  );
};

